import { Box, Button, Skeleton } from '@dev-spendesk/grapes';

import { useModal } from 'common/hooks/useModal';
import { useTranslation } from 'common/hooks/useTranslation';
import { useGetMemberAuthenticationFactors } from 'modules/authentication-factor/hooks/useGetMemberAuthenticationFactor';
import {
  ConfirmRequesterIdentityModal,
  type RecoveryShareMethod,
} from 'modules/members/components/ConfirmRequesterIdentityModal/ConfirmRequesterIdentityModal';
import { RecoveryFactorScaModalContainer } from 'modules/members/containers/RecoveryFactorScaModalContainer/RecoveryFactorScaModalContainer';
import { QuerySuspenseBase } from 'src/core/common/components/QuerySuspenseBase/QuerySuspenseBase';
import { UnexpectedErrorCallout } from 'src/core/common/components/UnexpectedErrorCallout/UnexpectedErrorCallout';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';
import { useUserId } from 'src/core/modules/app/hooks/useUserId';
import { AuthenticationFactorItem } from 'src/core/modules/authentication-factor/components/AuthenticationFactorItem';

import { type Member } from '../../models/member';

type Props = {
  member: Member;
};

export const MemberEditTabSecurity = ({ member }: Props) => {
  const { t } = useTranslation('global');
  const companyId = useCompanyId();
  const userId = useUserId();
  const authenticationFactorsQueryState = useGetMemberAuthenticationFactors({
    userId: member.id,
    companyId,
  });

  const [
    confirmRequesterIdentityModal,
    showConfirmRequesterIdentityModal,
    hideConfirmRequesterIdentityModal,
  ] = useModal(() => {
    return (
      <ConfirmRequesterIdentityModal
        onCancel={() => {
          hideConfirmRequesterIdentityModal();
        }}
        onConfirm={(shareMethod: RecoveryShareMethod) => {
          hideConfirmRequesterIdentityModal();
          showRecoveryFactorScaModal({
            member: { id: member.id, email: member.email },
            recoveryShareMethod: shareMethod,
          });
        }}
      />
    );
  });

  const [
    recoveryFactorScaModal,
    showRecoveryFactorScaModal,
    hideRecoveryFactorScaModal,
  ] = useModal<{
    member: { id: string; email: string };
    recoveryShareMethod: RecoveryShareMethod;
  }>(({ member: selectedMember, recoveryShareMethod }) => {
    return (
      <RecoveryFactorScaModalContainer
        member={selectedMember}
        onClose={() => {
          hideRecoveryFactorScaModal();
        }}
        recoveryShareMethod={recoveryShareMethod}
      />
    );
  });

  return (
    <>
      <div className="flex flex-col content-stretch gap-24">
        <div className="w-full pb-16 text-left">
          <h3 className="title-m text-content-primary mb-8">
            {t('members.editModal.security.factorsTitle')}
          </h3>
          <p className="text-content-secondary-bg-primary body-m">
            {t('members.editModal.security.factorsDescription')}
          </p>
          <QuerySuspenseBase
            queryState={authenticationFactorsQueryState}
            loading={
              <>
                <Skeleton height="76px" width="100%" />
                <Skeleton height="76px" width="100%" />
              </>
            }
            fallback={() => <UnexpectedErrorCallout />}
          >
            {({ factors }) => (
              <>
                <div className="mt-16 mb-16 flex flex-col gap-16">
                  {factors
                    .filter((factor) =>
                      ['TOTP', 'MOBILE_APP', 'SMS'].includes(factor.type),
                    )
                    .map((factor) => (
                      <AuthenticationFactorItem
                        revocationFlow="disabled"
                        key={factor.id}
                        id={factor.id}
                        name={factor.name}
                        lastUsedAt={factor.lastUsedAt}
                        type={factor.type}
                      />
                    ))}
                </div>
                {userId !== member.id && (
                  <Box className="flex flex-row p-16" variant="primary">
                    <p className="text-content-primary body-m">
                      {t('members.editModal.security.lostAccess')}
                    </p>
                    <Button
                      variant="tertiaryBrand"
                      text={t(
                        'members.editModal.security.generateRecoveryCode',
                      )}
                      onClick={showConfirmRequesterIdentityModal}
                    />
                  </Box>
                )}
              </>
            )}
          </QuerySuspenseBase>
        </div>
      </div>
      {confirmRequesterIdentityModal}
      {recoveryFactorScaModal}
    </>
  );
};
